/**
 * Responsive table component for markdown tables
 * Stacks cells vertically on mobile devices for better readability
 */

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";

import { MARKDOWN_STYLES } from "./constants";

/**
 * Props for responsive table components
 */
interface TableProps {
  children: React.ReactNode;
}

/**
 * Responsive table wrapper component
 * On desktop: renders standard MUI table
 * On mobile: stacks cells vertically with labels
 *
 * @param props - Table component props
 * @returns Rendered table element
 */
export const ResponsiveTable: React.FC<TableProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (isMobile) {
    return <Box sx={{ width: "100%", marginY: 2 }}>{children}</Box>;
  }

  return (
    <TableContainer sx={{ marginY: 2 }}>
      <Table sx={MARKDOWN_STYLES.table}>{children}</Table>
    </TableContainer>
  );
};

/**
 * Table head component
 */
export const MdTableHead: React.FC<TableProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (isMobile) {
    // Hide header on mobile (shown inline with each cell)
    return null;
  }

  return <TableHead>{children}</TableHead>;
};

/**
 * Table body component
 */
export const MdTableBody: React.FC<TableProps> = ({ children }) => {
  return <TableBody>{children}</TableBody>;
};

/**
 * Table row component
 */
export const MdTableRow: React.FC<TableProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (isMobile) {
    return (
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          paddingY: 2,
          "&:last-child": {
            borderBottom: 0,
          },
        }}
      >
        {children}
      </Box>
    );
  }

  return <TableRow>{children}</TableRow>;
};

/**
 * Table cell component (header and data)
 */
export const MdTableCell: React.FC<TableProps & { isHeader?: boolean }> = ({
  children,
  isHeader,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (isMobile) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: 1,
          ...(isHeader && {
            fontWeight: 600,
            fontSize: "0.875rem",
            color: "text.secondary",
          }),
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <TableCell
      sx={{
        ...MARKDOWN_STYLES.tableCell,
        ...(isHeader && {
          fontWeight: 600,
          backgroundColor: "grey.100",
        }),
      }}
    >
      {children}
    </TableCell>
  );
};
